/**
 * Analyst Guard
 *
 * Protects admin routes - only allows access to users with is_analyst flag.
 * Redirects non-analysts to home page.
 *
 * Note: Uses lazy injection to avoid circular dependency during app initialization
 */

import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { AuthStore } from '@app/core/auth/stores/auth-store';
import { isAnalyst } from '@app/core/auth/models/user.model';
import { devLog, devWarn, devError } from '@app/core/utils/dev-logger';

export const analystGuard: CanActivateFn = () => {
  let authStore: AuthStore;
  let router: Router;

  try {
    authStore = inject(AuthStore);
    router = inject(Router);
  } catch (error) {
    // If injection fails during initialization, redirect to login
    const fallbackRouter = inject(Router);
    return fallbackRouter.createUrlTree(['/login']);
  }

  // Check if user is authenticated
  if (!authStore.isLoggedIn()) {
    return router.createUrlTree(['/login']);
  }

  // Check if user has analyst flag
  const user = authStore.user();
  if (!isAnalyst(user)) {
    devWarn('Access denied: User is not an analyst');
    return router.createUrlTree(['/']); // Redirect to home
  }

  return true;
};
